import React from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import getConfig from 'getConfig';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import BaseListingRendererWithPlugins from 'nav/expanded/BaseListingRendererWithPlugins';
import {
  ColumnAccessorKey,
  ColumnConfiguration,
  ListingRendererType,
  ListingTypes,
} from 'nav/expanded/ListingRendererConstants';
import {getProcedureIcon} from 'procedures/design/procedureIconUtils';
import {SortKey} from 'search/SearchConstants';
import {getColumnHeaderFromSortKey} from 'search/SortUtil';
import PublishingStatusRenderer from 'template-collectibles/publishing-ui/ListingRenderers/PublishingStatusRenderer';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {TABLE_ROW_BODY_HEIGHT} from 'ui-library/scrollable/ScrollableTableConstants';
import {DatetimeTableCell} from 'ui-library/scrollable/table-cells/DatetimeTableCell';
import {BasicEvent} from 'util/Types';

import {TYPE_TO_LABEL} from '../TemplateCollectionPage';
import {getTemplateCollectibleType} from '../TemplateCollectionTable';

export default class CollectibleListingRenderer extends BaseListingRendererWithPlugins<ListingRendererType.COLLECTIBLE> {
  listingRendererType = ListingRendererType.COLLECTIBLE;

  getModelSpecificColumnConfigurations(): Array<
    ColumnConfiguration<ListingTypes[ListingRendererType.COLLECTIBLE]>
  > {
    return [];
  }

  getMetadataColumnConfigurations(): Array<
    ColumnConfiguration<ListingTypes[ListingRendererType.COLLECTIBLE]>
  > {
    const publishingStatusColumnConfig: ColumnConfiguration<ListingTypes[ListingRendererType.COLLECTIBLE]> = {
      column: {
        accessorKey: ColumnAccessorKey.PUBLISHING_STATUS,
        header: 'Publishing Status',
        flexGrow: 1,
        isCollapsible: false,
      },
      render: (item) => {
        const isPublishingSupportedForItemType =
          item.__typename === 'TemplateNode' ||
          item.__typename === 'SubtemplateNode' ||
          (item.__typename === 'ProcedureNode' && BenchlingConfig.PROCEDURES_ENABLE_PUBLISHING);
        const currentPublishingRecord = isPublishingSupportedForItemType
          ? item.currentPublishingRecord
          : null;
        if (currentPublishingRecord) {
          assert(
            !isInaccessible(currentPublishingRecord),
            'Expected current publishing record to be accessible.'
          );
        }
        return (
          <PublishingStatusRenderer
            isPublishingSupportedForItemType={isPublishingSupportedForItemType}
            currentPublishingRecord={currentPublishingRecord}
          />
        );
      },
    };

    return [
      {
        column: {
          accessorKey: ColumnAccessorKey.COLLECTION,
          header: 'Collection',
          flexGrow: 1,
          isCollapsible: false,
        },
        render: (item) => <div>{item.templateCollection.name}</div>,
      },
      ...(getConfig('ELN_ENABLE_TEMPLATE_PUBLISHING') ? [publishingStatusColumnConfig] : []),
      {
        column: {
          accessorKey: ColumnAccessorKey.CREATOR,
          // In the template listing, the title is displayed as Author even though the GraphQL is fetching the creator
          header: 'Author',
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_CREATOR_NAME,
        },
        render: (item) => item.creator.name,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.CREATED_AT,
          header: getColumnHeaderFromSortKey(SortKey.SORT_CREATED_AT_KEY),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_CREATED_AT_KEY,
        },
        render: (item) => <DatetimeTableCell datetime={item.createdAt} showTimeInCell={false} />,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.MODIFIED_AT,
          header: getColumnHeaderFromSortKey(SortKey.SORT_MODIFIED_AT_KEY),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_MODIFIED_AT_KEY,
        },
        render: (item) => <DatetimeTableCell datetime={item.modifiedAt} showTimeInCell={false} />,
      },
    ];
  }

  getNonPluginColumnConfigurations(
    items: Array<ListingTypes[ListingRendererType.COLLECTIBLE]>,
    extras: unknown
  ): Array<ColumnConfiguration<ListingTypes[ListingRendererType.COLLECTIBLE]>> {
    return [
      {
        column: {
          header: getColumnHeaderFromSortKey(SortKey.SORT_NAME_KEY),
          accessorKey: ColumnAccessorKey.NAME,
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_NAME_KEY,
        },
        render: (item) => <div>{item.name}</div>,
      },
      {
        column: {
          header: 'Type',
          accessorKey: ColumnAccessorKey.COLLECTIBLE_TYPE,
          flexGrow: 1,
          isSortable: false,
          isCollapsible: false,
        },
        render: (item) => <div>{TYPE_TO_LABEL.get(getTemplateCollectibleType(item))?.()}</div>,
      },
      ...this.getModelSpecificColumnConfigurations(),
      ...this.getMetadataColumnConfigurations(),
    ];
  }

  getNonPluginRowContentHeight(): number {
    return TABLE_ROW_BODY_HEIGHT;
  }

  openContextMenu(
    item: ListingTypes[ListingRendererType.COLLECTIBLE],
    e: BasicEvent,
    options: unknown
  ): void {}

  renderIcon(item: ListingTypes[ListingRendererType.COLLECTIBLE]): JSX.Element {
    return (
      <BenchlingIcon
        icon={getIcon(item.__typename, item.__typename === 'ProcedureNode' ? item.procedureType : undefined)}
        className="scrollableTable-cell-icon"
      />
    );
  }
}

export function getIcon(typename: string, procedureType?: ProcedureType): BenchlingIconName {
  if (typename === 'TemplateNode') {
    return ModelTypeConstants.TEMPLATE;
  } else if (typename === 'SubtemplateNode') {
    return ModelTypeConstants.SUBTEMPLATE;
  } else if (typename === 'ProcedureNode') {
    assert(procedureType != null, 'Procedure type expected for ProcedureNode');
    return getProcedureIcon(procedureType);
  } else if (typename === 'FormDefinitionNode') {
    return ModelTypeConstants.FORM_DEFINITION;
  } else if (typename === 'RequestV2DefinitionNode') {
    return ModelTypeConstants.REQUEST_V2_DEFINITION;
  } else if (typename === 'AnalysisTemplateNode') {
    return ModelTypeConstants.ANALYSIS_TEMPLATE;
  } else if (typename === 'PlateDesignTemplateNode') {
    return ModelTypeConstants.PLATE_DESIGN_TEMPLATE;
  } else {
    throw new Error(`Unexpected typename: ${typename}`);
  }
}
